import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Group, Modal, Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { formatHumanName, type MedplumClient } from '@medplum/core';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Patient, Practitioner } from '@medplum/fhirtypes';
import {
  therapyTypeAllowsMultipleOpenEpisodes,
  type PaymentTypeCode,
} from '@unify/fhir-model/resources/episode-of-care';
import { useEpisodeOfCareTypes } from '../../pages/calendar/episodeOfCareTypeCatalog';
import { fetchOpenEpisodesOfCare, openTherapyTypeCodes } from '../../lib/openEpisodeOfCare';
import {
  createFulfilledEpisodePair,
  organizationIdFromPatient,
  organizationIdFromPractitioner,
} from '../../lib/createFulfilledEpisodePair';
import { fetchAdminDirectory } from '../../lib/adminDirectoryService';
import { listPracticeIntakeProviders } from '../../pages/public/intake/listPracticeIntakeProviders';
import { ADMIN_EXTENSION_URLS } from '../../lib/adminDirectoryConstants';
import { formatPractitionerPreferredLabel } from '@unify/fhir-model/resources/practitioner';
import { loadPractitionerStaffLabelMap } from '../../lib/loadPractitionerPreferredLabels';
import { useCurrentUserPractitioner } from '../../hooks/useCurrentUserPractitioner';
import { usePermissions } from '../../contexts/PermissionContext';
import type { Role } from '../../lib/rbacRoles';
import {
  rolesGrantPracticeCalendarAdmin,
  rolesGrantSupervisorCalendarView,
  rolesGrantUnifyCalendarAdmin,
} from '../../features/calendar/calendarAccess';
import { useSupervisionScope } from '../../pages/supervision/hooks/useSupervisionScope';

export interface AddEpisodeOfCareModalProps {
  opened: boolean;
  onClose: () => void;
  onCreated?: () => void;
  /** When set, the client picker is hidden and this patient is used. */
  fixedPatientId?: string;
  title?: string;
}

const PAYMENT_TYPE_OPTIONS: { value: PaymentTypeCode; label: string }[] = [
  { value: 'insurance', label: 'Insurance' },
  { value: 'self-pay', label: 'Self-pay' },
];

function formatPatientLabel(p: Patient): string {
  const n = p.name?.[0];
  if (!n) return p.id ?? 'Client';
  return formatHumanName(n);
}

function orgIdFromProfile(profile: ReturnType<typeof useMedplumProfile>): string | undefined {
  if (profile?.resourceType !== 'Practitioner' || !profile.id) return undefined;
  const pract = profile as Practitioner;
  const fromExt = organizationIdFromPractitioner(pract);
  if (fromExt) return fromExt;
  const extOrg = pract.extension?.find((e) => e.url === ADMIN_EXTENSION_URLS.organization)
    ?.valueReference?.reference;
  return extOrg?.startsWith('Organization/') ? extOrg.replace('Organization/', '') : undefined;
}

async function loadPracticeScopedOptions(
  medplum: MedplumClient,
  organizationId: string,
  clientIdsByOrganizationId: Record<string, string[]>,
): Promise<{
  patientOptions: { value: string; label: string }[];
  providerOptions: { value: string; label: string }[];
}> {
  const allowedClientIds = new Set(clientIdsByOrganizationId[organizationId] ?? []);
  const orgRef = `Organization/${organizationId}`;

  const [patients, providers] = await Promise.all([
    medplum.searchResources('Patient', { _count: '200', _sort: 'name' }),
    listPracticeIntakeProviders(medplum, organizationId),
  ]);

  const patientOptions = patients
    .filter((p) => p.id && p.active !== false)
    .filter((p) => {
      if (allowedClientIds.has(p.id!)) return true;
      const account = p.meta?.account?.reference;
      const accounts = p.meta?.accounts?.map((a) => a.reference) ?? [];
      return account === orgRef || accounts.includes(orgRef);
    })
    .map((p) => ({ value: p.id!, label: formatPatientLabel(p) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const staffLabels = await loadPractitionerStaffLabelMap(
    medplum,
    providers.map((p) => p.id),
  );
  const providerOptions = providers
    .map((p) => ({ value: p.id, label: staffLabels.get(p.id) ?? p.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { patientOptions, providerOptions };
}

export function AddEpisodeOfCareModal({
  opened,
  onClose,
  onCreated,
  fixedPatientId,
  title = 'Add episode of care',
}: AddEpisodeOfCareModalProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role, availableRoles, activeRole } = usePermissions();
  const roles = useMemo(() => {
    const set = new Set<Role>();
    const slug = (activeRole ?? role) as Role | undefined;
    if (slug) set.add(slug);
    for (const r of availableRoles ?? []) set.add(r as Role);
    return [...set];
  }, [role, availableRoles, activeRole]);
  const isUnifyAdmin = rolesGrantUnifyCalendarAdmin(roles);
  const isPracticeAdmin = rolesGrantPracticeCalendarAdmin(roles);
  const isSupervisor = rolesGrantSupervisorCalendarView(roles);
  const providerLocked = !isUnifyAdmin && !isPracticeAdmin && !isSupervisor;
  const { superviseePractitionerIds } = useSupervisionScope();
  const profilePracticeId = orgIdFromProfile(profile);
  const { practitioner: currentPractitioner } = useCurrentUserPractitioner();
  const { types: episodeTypes } = useEpisodeOfCareTypes();

  const [therapyTypeCode, setTherapyTypeCode] = useState<string | null>(null);
  const [paymentTypeCode, setPaymentTypeCode] = useState<PaymentTypeCode | null>(null);
  const [practiceId, setPracticeId] = useState<string | null>(null);
  const [patientId, setPatientId] = useState<string | null>(null);
  const [practitionerId, setPractitionerId] = useState<string | null>(null);
  const [practiceOptions, setPracticeOptions] = useState<{ value: string; label: string }[]>([]);
  const [clientIdsByOrganizationId, setClientIdsByOrganizationId] = useState<
    Record<string, string[]>
  >({});
  const [patientOptions, setPatientOptions] = useState<{ value: string; label: string }[]>([]);
  const [preselectedPatientLabel, setPreselectedPatientLabel] = useState<string | null>(null);
  const [providerOptions, setProviderOptions] = useState<{ value: string; label: string }[]>([]);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingScoped, setLoadingScoped] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const practiceLocked = Boolean(fixedPatientId) || (!isUnifyAdmin && Boolean(profilePracticeId));

  const reset = useCallback(() => {
    setTherapyTypeCode(null);
    setPaymentTypeCode(null);
    setPracticeId(null);
    setPatientId(fixedPatientId ?? null);
    setPreselectedPatientLabel(null);
    setPractitionerId(null);
    setPracticeOptions([]);
    setClientIdsByOrganizationId({});
    setPatientOptions([]);
    setProviderOptions([]);
  }, [fixedPatientId]);

  useEffect(() => {
    if (!opened) return;
    reset();
    setLoadingInitial(true);
    void (async () => {
      try {
        const directory = await fetchAdminDirectory(medplum);
        setPracticeOptions(
          directory.practices
            .map((p) => ({ value: p.id, label: p.name }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        );
        setClientIdsByOrganizationId(directory.clientIdsByOrganizationId);

        let initialPracticeId = profilePracticeId ?? null;
        if (fixedPatientId) {
          const patient = await medplum.readResource('Patient', fixedPatientId);
          initialPracticeId = organizationIdFromPatient(patient) ?? initialPracticeId;
          setPreselectedPatientLabel(formatPatientLabel(patient));
          setPatientId(fixedPatientId);
          if (currentPractitioner?.id && !isUnifyAdmin && !isPracticeAdmin) {
            setPractitionerId(currentPractitioner.id);
          }
        }
        setPracticeId(initialPracticeId);
      } catch (err) {
        console.error('[AddEpisodeOfCareModal] load practices failed:', err);
        notifications.show({
          color: 'red',
          title: 'Could not load options',
          message: err instanceof Error ? err.message : 'Please try again.',
        });
      } finally {
        setLoadingInitial(false);
      }
    })();
  }, [
    opened,
    medplum,
    reset,
    fixedPatientId,
    profilePracticeId,
    currentPractitioner?.id,
    isUnifyAdmin,
    isPracticeAdmin,
  ]);

  useEffect(() => {
    if (!opened || !practiceId) {
      setPatientOptions([]);
      setProviderOptions([]);
      return;
    }

    let cancelled = false;
    setLoadingScoped(true);
    void (async () => {
      try {
        const scoped = await loadPracticeScopedOptions(
          medplum,
          practiceId,
          clientIdsByOrganizationId,
        );
        if (cancelled) return;
        setPatientOptions(scoped.patientOptions);
        let providers = scoped.providerOptions;
        if (providerLocked && currentPractitioner?.id) {
          providers = providers.filter((o) => o.value === currentPractitioner.id);
        } else if (isSupervisor && !isUnifyAdmin && !isPracticeAdmin) {
          const allowed = new Set<string>([
            ...(currentPractitioner?.id ? [currentPractitioner.id] : []),
            ...superviseePractitionerIds,
          ]);
          providers = providers.filter((o) => allowed.has(o.value));
        }
        setProviderOptions(providers);
        if (providerLocked && currentPractitioner?.id) {
          setPractitionerId(currentPractitioner.id);
        } else if (isSupervisor && !isUnifyAdmin && !isPracticeAdmin && currentPractitioner?.id) {
          setPractitionerId((prev) => prev ?? currentPractitioner.id ?? null);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[AddEpisodeOfCareModal] load scoped options failed:', err);
        notifications.show({
          color: 'red',
          title: 'Could not load clients or providers',
          message: err instanceof Error ? err.message : 'Please try again.',
        });
      } finally {
        if (!cancelled) {
          setLoadingScoped(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    opened,
    practiceId,
    medplum,
    clientIdsByOrganizationId,
    fixedPatientId,
    providerLocked,
    isSupervisor,
    isUnifyAdmin,
    isPracticeAdmin,
    currentPractitioner?.id,
    superviseePractitionerIds,
  ]);

  // Therapy types the selected client already has an open episode for are not selectable.
  const [openTypeCodes, setOpenTypeCodes] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!opened || !patientId) {
      setOpenTypeCodes(new Set());
      return;
    }
    let cancelled = false;
    void fetchOpenEpisodesOfCare(medplum, patientId)
      .then((episodes) => {
        if (!cancelled) setOpenTypeCodes(openTherapyTypeCodes(episodes));
      })
      .catch((err) => {
        console.error('[AddEpisodeOfCareModal] load open episodes failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [opened, patientId, medplum]);

  const episodeTypeOptions = useMemo(
    () =>
      episodeTypes
        .filter((t) => t.active)
        .map((t) => {
          const alreadyOpen = openTypeCodes.has(t.code) && !therapyTypeAllowsMultipleOpenEpisodes(t.code);
          return {
            value: t.code,
            label: alreadyOpen ? `${t.name} — already open for this client` : t.name,
            disabled: alreadyOpen,
          };
        }),
    [episodeTypes, openTypeCodes],
  );

  useEffect(() => {
    if (therapyTypeCode && episodeTypeOptions.some((o) => o.value === therapyTypeCode && o.disabled)) {
      setTherapyTypeCode(null);
    }
  }, [therapyTypeCode, episodeTypeOptions]);

  const handlePracticeChange = (value: string | null) => {
    setPracticeId(value);
    setPatientId(null);
    setPreselectedPatientLabel(null);
    setPractitionerId(null);
  };

  const clientSelectData = useMemo(() => {
    if (!patientId || patientOptions.some((option) => option.value === patientId)) {
      return patientOptions;
    }
    return [
      { value: patientId, label: preselectedPatientLabel ?? 'Selected client' },
      ...patientOptions,
    ];
  }, [patientOptions, patientId, preselectedPatientLabel]);

  const optionsLoading = loadingInitial || loadingScoped;
  const scopedFieldsDisabled = optionsLoading || !practiceId;
  const canSubmit = Boolean(
    therapyTypeCode && paymentTypeCode && practiceId && patientId && practitionerId,
  );

  const handleSubmit = async () => {
    if (!therapyTypeCode || !paymentTypeCode || !practiceId || !patientId || !practitionerId) {
      return;
    }
    setSubmitting(true);
    try {
      const [patient, practitioner] = await Promise.all([
        medplum.readResource('Patient', patientId),
        medplum.readResource('Practitioner', practitionerId),
      ]);
      const practitionerDisplay =
        formatPractitionerPreferredLabel(practitioner) || undefined;

      const requester =
        profile?.resourceType === 'Practitioner' && profile.id
          ? (() => {
              const display = formatPractitionerPreferredLabel(profile);
              return {
                reference: `Practitioner/${profile.id}` as const,
                ...(display ? { display } : {}),
              };
            })()
          : undefined;

      await createFulfilledEpisodePair({
        medplum,
        patient,
        therapyTypeCode,
        paymentTypeCode,
        practitionerId,
        practitionerDisplay,
        organizationId: practiceId,
        requester,
      });

      medplum.invalidateSearches('ServiceRequest');
      medplum.invalidateSearches('EpisodeOfCare');
      medplum.invalidateSearches('Patient');

      notifications.show({
        color: 'green',
        title: 'Episode of care created',
        message:
          'A fulfilled service request and active episode were created. The provider can schedule from their calendar or caseload.',
      });
      onCreated?.();
      onClose();
    } catch (err) {
      console.error('[AddEpisodeOfCareModal] create failed:', err);
      notifications.show({
        color: 'red',
        title: 'Could not create episode',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="md" centered>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Select a practice, then choose the client and provider. Creates a fulfilled service
          request and active episode with the provider as care manager.
        </Text>

        <Select
          label="Practice"
          placeholder="Select practice..."
          data={practiceOptions}
          value={practiceId}
          onChange={handlePracticeChange}
          searchable
          required
          disabled={loadingInitial || practiceLocked}
          nothingFoundMessage={loadingInitial ? 'Loading...' : 'No practices found'}
        />

        <Select
          label="Episode type"
          placeholder="Select episode type..."
          data={episodeTypeOptions}
          value={therapyTypeCode}
          onChange={setTherapyTypeCode}
          searchable
          required
          disabled={scopedFieldsDisabled}
        />

        <Select
          label="Payment type"
          description="How this episode will be billed"
          placeholder="Select payment type..."
          data={PAYMENT_TYPE_OPTIONS}
          value={paymentTypeCode}
          onChange={(value) => setPaymentTypeCode(value as PaymentTypeCode | null)}
          required
          disabled={scopedFieldsDisabled}
        />

        <Select
          label="Client"
          placeholder={practiceId ? 'Select client...' : 'Select a practice first'}
          data={clientSelectData}
          value={patientId}
          onChange={setPatientId}
          searchable
          required
          disabled={scopedFieldsDisabled || Boolean(fixedPatientId)}
          nothingFoundMessage={
            optionsLoading ? 'Loading...' : practiceId ? 'No clients found' : 'Select a practice first'
          }
        />

        <Select
          label="Provider"
          placeholder={practiceId ? 'Select provider...' : 'Select a practice first'}
          data={providerOptions}
          value={practitionerId}
          onChange={setPractitionerId}
          searchable
          required
          disabled={scopedFieldsDisabled || providerLocked}
          nothingFoundMessage={
            optionsLoading ? 'Loading...' : practiceId ? 'No providers found' : 'Select a practice first'
          }
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} loading={submitting} disabled={!canSubmit}>
            Create episode
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
